import { useState, useEffect } from 'react';
import { useAuthStore } from '@/store';
import { api } from '@/store';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Wallet, ArrowUpRight, ArrowDownLeft, Clock, Send, Loader2, Lock, TrendingUp, ShoppingCart, RefreshCw, FileText, Download, Info } from 'lucide-react';

export default function NexuPayPage() {
  const { user, refreshUser } = useAuthStore();
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [txPage, setTxPage] = useState(1);
  const [txTotal, setTxTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({});
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [wAmount, setWAmount] = useState('');
  const [wPixKey, setWPixKey] = useState('');
  const [wPixType, setWPixType] = useState('');
  const [wBenefName, setWBenefName] = useState('');
  const [wBenefDoc, setWBenefDoc] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);
  const [filter, setFilter] = useState('');
  const [creditPlans, setCreditPlans] = useState([]);
  const [renewalPlans, setRenewalPlans] = useState([]);
  // Transfer
  const [transferOpen, setTransferOpen] = useState(false);
  const [tfRecipient, setTfRecipient] = useState('');
  const [tfAmount, setTfAmount] = useState('');
  const [transferring, setTransferring] = useState(false);
  const [creditsOpen, setCreditsOpen] = useState(false);
  const [renewOpen, setRenewOpen] = useState(false);
  const [buying, setBuying] = useState(null);
  const [renewing, setRenewing] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const load = async () => {
    try {
      const [wRes, sRes] = await Promise.all([
        api.get('/nexupay/wallet'),
        api.get('/nexupay/settings'),
      ]);
      setWallet(wRes.data);
      setSettings(sRes.data);
      if (wRes.data.enabled) {
        loadTx(1);
        try { const cp = await api.get('/nexupay/credit-plans'); setCreditPlans(cp.data); } catch {}
        try { const rp = await api.get('/nexupay/renewal-plans'); setRenewalPlans(rp.data); } catch {}
      }
    } catch {} finally { setLoading(false); }
  };

  const loadTx = async (page = 1) => {
    try {
      const r = await api.get(`/nexupay/transactions?page=${page}&limit=10${filter ? `&type=${filter}` : ''}`);
      setTransactions(r.data.transactions);
      setTxTotal(r.data.total);
      setTxPage(page);
    } catch {}
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { if (wallet?.enabled) loadTx(1); }, [filter]);

  const handleWithdraw = async () => {
    const amt = parseFloat(wAmount);
    if (!amt || amt < (settings.min_withdraw || 10)) return toast.error(`Minimo: R$ ${(settings.min_withdraw || 10).toFixed(2)}`);
    if (settings.max_withdraw && settings.max_withdraw > 0 && amt > settings.max_withdraw) return toast.error(`Maximo: R$ ${settings.max_withdraw.toFixed(2)}`);
    if (!wPixKey.trim()) return toast.error('Informe a chave PIX');
    if (!wPixType) return toast.error('Selecione o tipo de chave PIX');
    if (!wBenefName.trim()) return toast.error('Nome do beneficiario obrigatorio');
    if (!wBenefDoc.trim()) return toast.error('CPF/CNPJ do beneficiario obrigatorio');
    setWithdrawing(true);
    try {
      await api.post('/nexupay/withdraw', { amount: amt, pix_key: wPixKey, pix_key_type: wPixType, beneficiary_name: wBenefName.trim(), beneficiary_document: wBenefDoc.replace(/\D/g, '') });
      toast.success('Saque solicitado! Aguarde aprovacao.');
      setWithdrawOpen(false); setWAmount(''); setWPixKey(''); setWPixType(''); setWBenefName(''); setWBenefDoc('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
    finally { setWithdrawing(false); }
  };

  const handleBuyCredits = async (planId) => {
    setBuying(planId);
    try {
      const r = await api.post(`/nexupay/buy-credits/${planId}`);
      toast.success(r.data.message);
      setCreditsOpen(false);
      load();
      if (refreshUser) refreshUser();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
    finally { setBuying(null); }
  };

  const handleRenewPanel = async (planId) => {
    setRenewing(planId);
    try {
      const r = await api.post(`/nexupay/renew-panel/${planId}`);
      toast.success(r.data.message);
      setRenewOpen(false);
      load();
      if (refreshUser) refreshUser();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
    finally { setRenewing(null); }
  };

  const handleTransfer = async () => {
    if (!tfRecipient.trim()) { toast.error('Informe o usuario destinatario'); return; }
    const amt = parseFloat(tfAmount);
    if (!amt || amt <= 0) { toast.error('Valor invalido'); return; }
    setTransferring(true);
    try {
      const r = await api.post('/nexupay/transfer', { recipient: tfRecipient.trim(), amount: amt });
      toast.success(r.data.message);
      setTransferOpen(false); setTfRecipient(''); setTfAmount('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
    finally { setTransferring(false); }
  };


  const handleDownloadPdf = async (period = 'all') => {
    setPdfLoading(true);
    try {
      const r = await api.get(`/nexupay/statement-pdf?period=${period}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `extrato_nexupay_${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF baixado!');
    } catch (e) { toast.error('Erro ao gerar PDF'); }
    finally { setPdfLoading(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  if (!wallet?.enabled) return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <Wallet className="w-12 h-12 text-muted-foreground mb-4" />
      <h2 className="text-xl font-bold text-foreground mb-2">NexuPay</h2>
      <p className="text-sm text-muted-foreground">Sua carteira digital ainda nao foi habilitada.<br />Solicite ao administrador para ativar.</p>
    </div>
  );

  const txIcon = (t) => t === 'commission' ? <ArrowDownLeft className="w-4 h-4 text-emerald-500" /> : t.includes('transfer_in') ? <ArrowDownLeft className="w-4 h-4 text-blue-500" /> : t.includes('transfer_out') ? <ArrowUpRight className="w-4 h-4 text-blue-500" /> : t.includes('credit') ? <ArrowDownLeft className="w-4 h-4 text-green-500" /> : t.includes('withdraw') ? <ArrowUpRight className="w-4 h-4 text-red-500" /> : <Clock className="w-4 h-4 text-yellow-500" />;
  const txColor = (s) => s === 'completed' ? 'text-green-500' : s === 'pending' ? 'text-yellow-500' : 'text-red-500';
  const available = (wallet.balance || 0) - (wallet.blocked_balance || 0);

  return (
    <div className="space-y-6" data-testid="nexupay-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">NexuPay</h1>
          <p className="text-sm text-muted-foreground">Sua carteira digital</p>
        </div>
        <div className="relative">
          <Button variant="outline" size="sm" onClick={() => handleDownloadPdf('all')} disabled={pdfLoading} data-testid="pdf-download-btn">
            {pdfLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FileText className="w-4 h-4 mr-1" />}
            Extrato PDF
          </Button>
        </div>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border-emerald-500/20">
          <CardContent className="p-5">
            <p className="text-xs text-emerald-500/80 mb-1">Saldo Disponivel</p>
            <p className="text-2xl font-black text-emerald-500" data-testid="nexupay-balance">R$ {available.toFixed(2)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><Lock className="w-3 h-3" />Bloqueado (saques pendentes)</p>
            <p className="text-2xl font-black text-yellow-500">R$ {(wallet.blocked_balance || 0).toFixed(2)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><TrendingUp className="w-3 h-3" />Total Recebido</p>
            <p className="text-2xl font-black text-foreground">R$ {(wallet.total_received || 0).toFixed(2)}</p>
            <p className="text-[10px] text-muted-foreground mt-1">Taxas: R$ {(wallet.total_fees || 0).toFixed(2)} | Sacado: R$ {(wallet.total_withdrawn || 0).toFixed(2)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Taxas do NexuPay — deixa claro para o revendedor */}
      <Card className="border-cyan-500/20 bg-cyan-500/[0.03]">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Info className="w-4 h-4 text-cyan-400" />
            <h3 className="text-sm font-semibold text-foreground">Taxas do NexuPay</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="p-3 rounded-lg bg-background/50 border border-border/50">
              <p className="text-[11px] text-muted-foreground">Por recebimento</p>
              <p className="text-lg font-bold text-foreground">R$ {(settings.receive_fee ?? 1.5).toFixed(2)}</p>
              <p className="text-[10px] text-muted-foreground">descontado de cada pagamento que você recebe</p>
            </div>
            <div className="p-3 rounded-lg bg-background/50 border border-border/50">
              <p className="text-[11px] text-muted-foreground">Por saque</p>
              <p className="text-lg font-bold text-foreground">R$ {(settings.withdraw_fee ?? 1.5).toFixed(2)}</p>
              <p className="text-[10px] text-muted-foreground">descontado de cada saque solicitado</p>
            </div>
            <div className="p-3 rounded-lg bg-background/50 border border-border/50">
              <p className="text-[11px] text-muted-foreground">Saque mínimo</p>
              <p className="text-lg font-bold text-foreground">R$ {(settings.min_withdraw ?? 10).toFixed(2)}</p>
              <p className="text-[10px] text-muted-foreground">valor mínimo para solicitar um saque</p>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground mt-3">
            💡 Exemplo: ao receber R$ 100,00, entra R$ {(100 - (settings.receive_fee ?? 1.5)).toFixed(2)} na sua carteira. Ao sacar, é descontada a taxa de saque.
          </p>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => setTransferOpen(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="transfer-btn">
          <Send className="w-4 h-4 mr-2" />Transferir
        </Button>
        <Button onClick={() => setWithdrawOpen(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="withdraw-btn">
          <ArrowUpRight className="w-4 h-4 mr-2" />Solicitar Saque
        </Button>
        {creditPlans.length > 0 && (
          <Button onClick={() => setCreditsOpen(true)} variant="outline" data-testid="buy-credits-btn">
            <ShoppingCart className="w-4 h-4 mr-2" />Comprar Creditos
          </Button>
        )}
        {renewalPlans.length > 0 && (
          <Button onClick={() => setRenewOpen(true)} variant="outline" data-testid="renew-panel-btn">
            <RefreshCw className="w-4 h-4 mr-2" />Renovar Painel
          </Button>
        )}
      </div>

      {/* Transactions */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-sm">Extrato ({txTotal})</CardTitle>
            <div className="flex gap-1">
              {[
                { v: '', l: 'Tudo' },
                { v: 'credit', l: 'Entradas' },
                { v: 'transfer', l: 'Transferencias' },
                { v: 'debit_credits', l: 'Creditos' },
                { v: 'debit_renewal', l: 'Renovacao' },
                { v: 'withdraw_request', l: 'Saques' },
              ].map(f => (
                <button key={f.v} onClick={() => setFilter(f.v)} className={`px-2.5 py-1 text-[10px] rounded-md ${filter === f.v ? 'bg-primary text-primary-foreground' : 'bg-muted/30 text-muted-foreground hover:bg-muted/50'}`}>
                  {f.l}
                </button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {transactions.length === 0 ? <p className="text-sm text-muted-foreground text-center py-6">Nenhuma movimentacao</p> : (
            <div className="space-y-2">
              {transactions.map(tx => (
                <div key={tx.id} className="flex items-center gap-3 p-3 rounded-lg bg-muted/20 border border-border/50">
                  <div className="w-8 h-8 rounded-full bg-muted/50 flex items-center justify-center shrink-0">{txIcon(tx.type)}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">{tx.description}</p>
                    <p className="text-[10px] text-muted-foreground">{new Date(tx.created_at).toLocaleString('pt-BR')}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`text-sm font-bold ${['credit', 'commission', 'transfer_in'].includes(tx.type) ? 'text-green-500' : 'text-red-500'}`}>
                      {['credit', 'commission', 'transfer_in'].includes(tx.type) ? '+' : '-'}R$ {(tx.net_amount || 0).toFixed(2)}
                    </p>
                    {tx.fee > 0 && <p className="text-[10px] text-muted-foreground">Taxa: R$ {(tx.fee || 0).toFixed(2)}</p>}
                    <Badge className={`text-[9px] ${txColor(tx.status)}`} variant="outline">{tx.status === 'completed' ? 'Concluido' : tx.status === 'pending' ? 'Pendente' : 'Recusado'}</Badge>
                  </div>
                </div>
              ))}
              {txTotal > 10 && (
                <div className="flex justify-center gap-2 pt-2">
                  <Button variant="outline" size="sm" disabled={txPage <= 1} onClick={() => loadTx(txPage - 1)}>Anterior</Button>
                  <span className="text-xs text-muted-foreground py-2">Pag {txPage} de {Math.ceil(txTotal / 10)}</span>
                  <Button variant="outline" size="sm" disabled={txPage * 10 >= txTotal} onClick={() => loadTx(txPage + 1)}>Proxima</Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Withdraw Dialog */}
      <Dialog open={withdrawOpen} onOpenChange={setWithdrawOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Send className="w-5 h-5 text-emerald-500" />Solicitar Saque PIX</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="p-3 rounded-lg bg-muted/30 text-center">
              <p className="text-xs text-muted-foreground">Disponivel</p>
              <p className="text-xl font-bold text-emerald-500">R$ {available.toFixed(2)}</p>
              <p className="text-[10px] text-muted-foreground mt-1">Taxa por saque: R$ {(settings.withdraw_fee || 0).toFixed(2)} | Minimo: R$ {(settings.min_withdraw || 10).toFixed(2)}{settings.max_withdraw > 0 ? ` | Maximo: R$ ${settings.max_withdraw.toFixed(2)}` : ''}</p>
            </div>
            <div>
              <Label className="text-xs">Valor (R$)</Label>
              <Input type="number" min={settings.min_withdraw || 10} step="0.01" value={wAmount} onChange={e => setWAmount(e.target.value)} placeholder="0.00" className="h-10 text-lg font-bold mt-1" autoFocus />
              {wAmount && parseFloat(wAmount) > 0 && (
                <p className="text-[10px] text-muted-foreground mt-1">Voce recebera: R$ {parseFloat(wAmount).toFixed(2)} | Debito total: R$ {(parseFloat(wAmount) + (settings.withdraw_fee || 1.5)).toFixed(2)}</p>
              )}
            </div>
            <div>
              <Label className="text-xs">Chave PIX</Label>
              <Input value={wPixKey} onChange={e => setWPixKey(e.target.value)} placeholder="CPF, Email, Telefone ou Chave Aleatoria" className="h-9 mt-1" />
            </div>
            <div>
              <Label className="text-xs">Tipo da chave PIX *</Label>
              <select value={wPixType} onChange={e => setWPixType(e.target.value)} className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm mt-1" required>
                <option value="">Selecione o tipo</option>
                <option value="cpf">CPF</option>
                <option value="cnpj">CNPJ</option>
                <option value="email">E-mail</option>
                <option value="phone">Telefone</option>
                <option value="random">Chave Aleatoria</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Nome do beneficiario *</Label>
              <Input value={wBenefName} onChange={e => setWBenefName(e.target.value)} placeholder="Nome completo" className="h-9 mt-1" />
            </div>
            <div>
              <Label className="text-xs">CPF/CNPJ do beneficiario *</Label>
              <Input value={wBenefDoc} onChange={e => setWBenefDoc(e.target.value)} placeholder="Apenas numeros" className="h-9 mt-1" />
            </div>
            <Button onClick={handleWithdraw} disabled={withdrawing} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-withdraw-btn">
              {withdrawing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Solicitar Saque'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Buy Credits Dialog */}
      <Dialog open={creditsOpen} onOpenChange={setCreditsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-blue-500" />Comprar Creditos com NexuPay
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="p-3 rounded-lg bg-emerald-500/10 text-center">
              <p className="text-xs text-emerald-500/80">Saldo Disponivel</p>
              <p className="text-lg font-bold text-emerald-500">R$ {available.toFixed(2)}</p>
            </div>
            <p className="text-xs text-muted-foreground">Selecione um pacote para comprar usando seu saldo NexuPay. Sem taxas adicionais.</p>
            {creditPlans.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">Nenhum pacote disponivel</p>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {creditPlans.map(plan => {
                  const price = parseFloat(plan.price || 0);
                  const credits = parseInt(plan.credits || 0);
                  const canBuy = available >= price;
                  return (
                    <div key={plan.id} className={`flex items-center justify-between p-3 rounded-lg border ${canBuy ? 'border-border/50 bg-muted/20' : 'border-red-500/20 bg-red-500/5 opacity-60'}`}>
                      <div>
                        <p className="text-sm font-semibold">{plan.name || `${credits} Creditos`}</p>
                        <p className="text-xs text-muted-foreground">{credits} creditos</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <p className="text-sm font-bold">R$ {price.toFixed(2)}</p>
                        <Button
                          size="sm"
                          disabled={!canBuy || buying === plan.id}
                          onClick={() => handleBuyCredits(plan.id)}
                          className="h-8"
                          data-testid={`buy-plan-${plan.id}`}
                        >
                          {buying === plan.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Comprar'}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Renew Panel Dialog */}
      <Dialog open={renewOpen} onOpenChange={setRenewOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-purple-500" />Renovar Painel com NexuPay
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="p-3 rounded-lg bg-emerald-500/10 text-center">
              <p className="text-xs text-emerald-500/80">Saldo Disponivel</p>
              <p className="text-lg font-bold text-emerald-500">R$ {available.toFixed(2)}</p>
            </div>
            <p className="text-xs text-muted-foreground">Selecione um plano para renovar seu painel usando o saldo NexuPay. Sem taxas adicionais.</p>
            {renewalPlans.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">Nenhum plano disponivel</p>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {renewalPlans.map(plan => {
                  const price = parseFloat(plan.price || 0);
                  const months = parseInt(plan.duration_months || 1);
                  const canBuy = available >= price;
                  return (
                    <div key={plan.id} className={`flex items-center justify-between p-3 rounded-lg border ${canBuy ? 'border-border/50 bg-muted/20' : 'border-red-500/20 bg-red-500/5 opacity-60'}`}>
                      <div>
                        <p className="text-sm font-semibold">{plan.name || `Plano ${months}m`}</p>
                        <p className="text-xs text-muted-foreground">{months} {months === 1 ? 'mes' : 'meses'}</p>
                        {plan.connections && <p className="text-[10px] text-muted-foreground">{plan.connections} conexoes</p>}
                      </div>
                      <div className="flex items-center gap-3">
                        <p className="text-sm font-bold">R$ {price.toFixed(2)}</p>
                        <Button
                          size="sm"
                          disabled={!canBuy || renewing === plan.id}
                          onClick={() => handleRenewPanel(plan.id)}
                          className="h-8 bg-purple-600 hover:bg-purple-700"
                          data-testid={`renew-plan-${plan.id}`}
                        >
                          {renewing === plan.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Renovar'}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Transferir NexuPay</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-xs text-muted-foreground">Transferencia instantanea, sem taxa. O valor cai na hora na carteira do destinatario.</p>
            <div>
              <Label className="text-xs">Usuario destinatario *</Label>
              <Input value={tfRecipient} onChange={e => setTfRecipient(e.target.value)} placeholder="Username do destinatario" className="h-9 mt-1" />
            </div>
            <div>
              <Label className="text-xs">Valor (R$) *</Label>
              <Input type="number" step="0.01" min="0.01" value={tfAmount} onChange={e => setTfAmount(e.target.value)} placeholder="0.00" className="h-9 mt-1" />
              <p className="text-[10px] text-muted-foreground mt-1">Disponivel: R$ {(wallet ? (wallet.balance - (wallet.blocked_balance || 0)) : 0).toFixed(2)}</p>
            </div>
            <Button onClick={handleTransfer} disabled={transferring || !tfRecipient.trim() || !tfAmount} className="w-full bg-blue-600 hover:bg-blue-700 h-10">
              {transferring ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              {transferring ? 'Transferindo...' : 'Transferir'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
